import { warn } from "../core/config.js";
import type { FacetOption, MatchMode } from "../core/types.js";
import type { SlaterList } from "../list.js";

/**
 * Filtering — `[data-slater-filter]` controls, `[data-slater-sort]`, and the
 * server-computed facets that auto-fill empty selects. List mode only: the
 * global-search endpoint takes no filter/sort params.
 */

/**
 * The filter value a control contributes when active.
 *
 * `data-slater-value` wins over the native `value`, because Webflow reserves
 * `value` as a custom-attribute name on checkboxes/radios — you can't set it in
 * the Designer. This is the same reason Finsweet reads `fs-list-value`.
 */
function controlValues(control: Element): string[] {
  if (control instanceof HTMLSelectElement) {
    return Array.from(control.selectedOptions)
      .map((o) => o.getAttribute("data-slater-value") ?? o.value)
      .filter(Boolean);
  }
  if (control instanceof HTMLInputElement) {
    const value = control.getAttribute("data-slater-value") ?? control.value;
    return control.checked && value ? [value] : [];
  }
  return [];
}

function syncFilterField(list: SlaterList, field: string): void {
  const controls = list.filterControls.get(field) ?? [];
  const values = controls.flatMap(controlValues);
  if (values.length > 0) list.state.filters[field] = values;
  else delete list.state.filters[field];
}

/** Binds filter and sort controls (list mode); warns when pointed at a search block. */
export function bindFilterControls(list: SlaterList): void {
  if (list.config.mode === "list") {
    for (const control of list.scoped("[data-slater-filter]")) {
      const field = control.getAttribute("data-slater-filter");
      if (!field) continue;
      const group = list.filterControls.get(field) ?? [];
      group.push(control);
      list.filterControls.set(field, group);

      const mode = control.getAttribute("data-slater-filter-match");
      if (mode === "all" || mode === "any") list.state.matchByField[field] = mode as MatchMode;

      control.addEventListener("change", () => {
        syncFilterField(list, field);
        list.resetAndLoad();
      });
    }

    for (const select of list.scoped("[data-slater-sort]")) {
      select.addEventListener("change", () => {
        list.state.sort = (select as HTMLSelectElement).value || list.config.sort;
        list.resetAndLoad();
      });
    }
  } else if (
    list.scoped("[data-slater-filter]").length > 0 ||
    list.scoped("[data-slater-sort]").length > 0
  ) {
    warn(`${list.label}: filters and sorting are not supported with global site search yet.`);
  }
}

/**
 * Fills `<select data-slater-filter>` options from server-computed facets when
 * the designer left the control empty. Counts come from the whole collection,
 * so a zero-count option can still be offered — which a DOM-bound tool cannot
 * know, since it only ever sees the items Webflow rendered.
 */
export function applyFacets(
  list: SlaterList,
  facets: Record<string, FacetOption[]> | undefined,
): void {
  if (!facets) return;
  for (const [field, options] of Object.entries(facets)) {
    for (const control of list.filterControls.get(field) ?? []) {
      if (!(control instanceof HTMLSelectElement)) continue;
      if (control.dataset.slaterPopulated !== "true" && control.options.length > 1) continue;

      const selected = control.value;
      const placeholder = control.options[0]?.cloneNode(true) as HTMLOptionElement | undefined;
      control.replaceChildren();
      if (placeholder) control.appendChild(placeholder);
      for (const option of options) {
        const el = document.createElement("option");
        el.value = option.value;
        el.textContent = option.label;
        control.appendChild(el);
      }
      control.dataset.slaterPopulated = "true";
      control.value = selected;
    }
  }
}
